import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';
import { 
  LineChart, Line, PieChart, Pie, Tooltip, Legend, Cell, 
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, BarChart, Bar 
} from 'recharts';
import {
  User, Calendar, Clock, AlertTriangle, Save, X, Edit3, Mail, 
  AlertCircle, CheckCircle, Activity,
  Trash2, AlertOctagon, Upload, Key, LogOut, RefreshCw,
  FileText, Shield, Settings, Camera, Check, UserX, UserCheck,
  Home, ChevronDown, ChevronRight, Building, MapPin
} from 'lucide-react';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

const STATUS_TYPES = {
  ACTIVE: { color: '#10b981', label: 'Aktiv' },
  CHECKEDOUT: { color: '#3b82f6', label: 'Abgemeldet' },
  SICK: { color: '#ef4444', label: 'Krank' },
  ABSENT: { color: '#f97316', label: 'Nicht anwesend' },
  'Nicht-Abgemeldet': { color: '#f59e0b', label: 'Nicht Abgemeldet' }
};

const UserModal = ({ user = null, onClose, onUpdate }) => {
  // Tabs and Views
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileView, setMobileView] = useState(false);
  
  // Data states
  const [statusHistory, setStatusHistory] = useState([]);
  const [roomInfo, setRoomInfo] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // User edit states
  const [editedUsername, setEditedUsername] = useState('');
  const [editedProfileImage, setEditedProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Password change states
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Confirmation dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isEditingSelf, setIsEditingSelf] = useState(false);
  
  // Loading states
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  
  // Errors
  const [deleteError, setDeleteError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [logoutError, setLogoutError] = useState(null);
  
  // Profile image handling
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const fileInputRef = useRef(null);

  // History display state
  const [historyByDay, setHistoryByDay] = useState({});
  const [expandedDays, setExpandedDays] = useState({});

  // Screen size detection
  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 768);
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check if current user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = await localforage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${BASE_URL}/api/auth/get-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch profile');
        
        const data = await response.json();
        setCurrentUser(data.user);
        setIsAdmin(data.user.role === 'ROLE_ADMIN');
        setIsEditingSelf(data.user.email === user.email);
      } catch (err) {
        console.error('Error checking admin status:', err);
      }
    };
    
    if (user?.id) {
      checkAdminStatus();
    }
  }, [user?.id, user?.email]);

  // Initialize data when user changes
  useEffect(() => {
    if (user?.id) {
      fetchUserData();
      fetchRoomInfo();
      loadProfileImage();
      // Initialize form values
      setEditedUsername(user.username || '');
    }
  }, [user?.id]);

  // Group history by day when statusHistory changes
  useEffect(() => {
    if (statusHistory.length > 0) {
      const groupedByDay = groupHistoryByDay(statusHistory);
      setHistoryByDay(groupedByDay);
      
      // Initialize all days as expanded
      const initialExpandedState = {};
      Object.keys(groupedByDay).forEach(day => {
        initialExpandedState[day] = true;
      });
      setExpandedDays(initialExpandedState);
    }
  }, [statusHistory]);

  // Group status history by day
  const groupHistoryByDay = (history) => {
    const grouped = {};
    
    // Sort history by timestamp (newest first)
    const sortedHistory = [...history].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    // Group by day
    sortedHistory.forEach(record => {
      const date = new Date(record.timestamp);
      const day = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      if (!grouped[day]) {
        grouped[day] = [];
      }
      
      grouped[day].push(record);
    });
    
    // Sort each day's records by timestamp (oldest first for timeline)
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => 
        new Date(a.timestamp) - new Date(b.timestamp)
      );
    });
    
    return grouped;
  };

  // Load profile image with error handling
  const loadProfileImage = async () => {
    if (!user?.profileImagePath) {
      setImageLoaded(false);
      setProfileImageUrl('/api/placeholder/100/100');
      return;
    }

    try {
      const timestamp = new Date().getTime();
      let imageUrl = user.profileImagePath;
      
      // Add proper URL if it's just a path
      if (!imageUrl.startsWith('http')) {
        imageUrl = `${BASE_URL}${imageUrl}?t=${timestamp}`;
      }
      
      // Check if image loads
      const img = new Image();
      img.onload = () => {
        setProfileImageUrl(imageUrl);
        setImageLoaded(true);
      };
      
      img.onerror = () => {
        setImageLoaded(false);
        setProfileImageUrl('/api/placeholder/100/100');
      };
      
      img.src = imageUrl;
    } catch (err) {
      setImageLoaded(false);
      setProfileImageUrl('/api/placeholder/100/100');
    }
  };

  // Fetch user data including history and statistics
  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      // Fetch user statistics
      const statsResponse = await fetch(
        `${BASE_URL}/api/users/${user.id}/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!statsResponse.ok) throw new Error('Failed to fetch user statistics');
      const statsData = await statsResponse.json();
      setStatistics(statsData);

      // Fetch status history
      const historyResponse = await fetch(
        `${BASE_URL}/api/auth/status-history/email/${user.email}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!historyResponse.ok) throw new Error('Failed to fetch status history');
      const historyData = await historyResponse.json();
      setStatusHistory(historyData.history || []);

      setLoading(false);
    } catch (err) {
      setError('Failed to load user data: ' + err.message);
      setLoading(false);
    }
  };

  // Fetch user's room information
  const fetchRoomInfo = async () => {
    try {
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      // Fetch room information for the user
      const response = await fetch(
        `${BASE_URL}/api/rooms/user/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setRoomInfo(data);
      } else if (response.status === 404) {
        // User might not have a room assigned yet
        setRoomInfo({ message: 'No room assigned' });
      } else {
        // Other API errors
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch room information');
      }
    } catch (err) {
      console.error('Error fetching room info:', err);
      // We don't set the main error state here to avoid blocking the entire modal
      // if just the room info fails to load
    }
  };

  // Toggle expanded state for a day
  const toggleDayExpand = (day) => {
    setExpandedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Update user status
  const updateUserStatus = async (status) => {
    try {
      setStatusLoading(true);
      setStatusError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${BASE_URL}/api/auth/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: user.email, 
          status: status 
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update status');
      }

      const data = await response.json();
      
      // Show success message and refresh data
      setSuccessMessage(`Status updated to ${STATUS_TYPES[status]?.label || status}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Close confirmation dialog
      setShowStatusConfirm(false);
      
      // Refresh data
      fetchUserData();
      
      // Notify parent component of the update
      if (onUpdate) {
        onUpdate({ ...user, status: status });
      }
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusLoading(false);
    }
  };

  // Force user to logout (revoke access)
  const forceUserLogout = async () => {
    try {
      setLogoutLoading(true);
      setLogoutError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');

      // This endpoint would invalidate any tokens for this user
      // You'll need to implement this on the backend
      const response = await fetch(`${BASE_URL}/api/auth/force-logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: user.email })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to force logout');
      }

      // Show success message
      setSuccessMessage('User was forced to logout successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Close confirmation dialog
      setShowLogoutConfirm(false);
    } catch (err) {
      setLogoutError(err.message || 'Error forcing user logout');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');
      
      // API call to delete the user
      const response = await fetch(`${BASE_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error deleting user');
      }
      
      // Successful deletion
      setShowDeleteConfirm(false);
      
      // Callback to update the user list
      if (onUpdate) {
        onUpdate();
      }
      
      // Close modal
      onClose();
      
    } catch (err) {
      setDeleteError(err.message);
      setDeleteLoading(false);
    }
  };

  // Handle admin password change
  const handleAdminPasswordChange = async () => {
    try {
      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords don't match");
        return;
      }
      
      if (newPassword.length < 6) {
        setPasswordError("Password must be at least 6 characters long");
        return;
      }
      
      setPasswordChangeLoading(true);
      setPasswordError('');
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');
      
      const response = await fetch(`${BASE_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          newPassword: newPassword
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to change password');
      }
      
      // Show success message
      setSuccessMessage("User's password changed successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reset password fields
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordField(false);
      
      // Switch back to overview tab
      setTimeout(() => setActiveTab('overview'), 1000);
      
      // Update parent component
      if (onUpdate) {
        onUpdate();
      }
      
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Save profile changes
  const saveProfileChanges = async () => {
    try {
      setEditLoading(true);
      setEditError(null);
      
      const token = await localforage.getItem('token');
      if (!token) throw new Error('No token found');
      
      // If admin is editing another user's username
      if (isAdmin && !isEditingSelf) {
        // Create form data for multipart upload
        const formData = new FormData();
        
        // For updating another user's profile, send both emails
        formData.append('username', editedUsername);
        formData.append('email', user.email);
        
        // Add profile image if selected
        if (editedProfileImage) {
          formData.append('profileImage', editedProfileImage);
        }
        
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev < 90) return prev + 10;
            clearInterval(progressInterval);
            return prev;
          });
        }, 200);
        
        // Use an admin-specific endpoint for updating other users
        // Note: This endpoint needs to be implemented on the backend
        const response = await fetch(`${BASE_URL}/api/admin/update-user`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        });
        
        if (!response.ok) {
          // If the admin endpoint doesn't exist, show a specific error
          if (response.status === 404) {
            throw new Error('Admin user update endpoint not available. Backend changes required.');
          }
          
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update user profile');
        }
        
        // Set upload progress to 100% when complete
        setUploadProgress(100);
        
        // Show success message
        setSuccessMessage('User profile updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Reset form
        setEditedProfileImage(null);
        setImagePreview('');
        
        // Reset progress after a delay
        setTimeout(() => setUploadProgress(0), 1000);
        
        // Update parent component
        if (onUpdate) {
          onUpdate();
        }
        
        // Switch back to overview tab
        setTimeout(() => setActiveTab('overview'), 1000);
      } else {
        // Original logic for updating own profile
        const formData = new FormData();
        formData.append('username', editedUsername);
        formData.append('email', user.email);
        
        // Add profile image if selected
        if (editedProfileImage) {
          formData.append('profileImage', editedProfileImage);
        }
        
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev < 90) return prev + 10;
            clearInterval(progressInterval);
            return prev;
          });
        }, 200);
        
        // Update profile
        const response = await fetch(`${BASE_URL}/api/auth/update-profile`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update profile');
        }
        
        // Set upload progress to 100% when complete
        setUploadProgress(100);
        
        // Show success message
        setSuccessMessage('Profile updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Reset form
        setEditedProfileImage(null);
        setImagePreview('');
        
        // Reset progress after a delay
        setTimeout(() => setUploadProgress(0), 1000);
        
        // Update parent component
        if (onUpdate) {
          onUpdate();
        }
        
        // Switch back to overview tab
        setTimeout(() => setActiveTab('overview'), 1000);
      }
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedProfileImage(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const handleImagePickerClick = () => {
    fileInputRef.current.click();
  };

  // Render status distribution chart
  const renderStatusChart = () => {
    if (!statusHistory.length) return null;

    const statusCounts = statusHistory.reduce((acc, record) => {
      acc[record.newStatus] = (acc[record.newStatus] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.entries(statusCounts).map(([status, count]) => ({
      name: STATUS_TYPES[status]?.label || status,
      value: count,
      status: status
    }));

    return (
      <div style={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {pieData.map((entry, index) => (
                <Cell 
                  key={index} 
                  fill={STATUS_TYPES[entry.status]?.color || '#6b7280'} 
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Get final status of a day
  const getFinalDayStatus = (records) => {
    if (!records || records.length === 0) return null;
    return records[records.length - 1].newStatus;
  };

  // Render timeline chart
  const renderTimelineChart = () => {
    if (!statusHistory.length) return null;

    const timelineData = statusHistory.map(record => ({
      date: new Date(record.timestamp).toLocaleDateString(),
      status: record.newStatus,
      updatedBy: record.updatedBy
    }));

    return (
      <div style={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <Tooltip />
            <Line
              type="stepAfter"
              dataKey="status"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ fill: '#8884d8' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render history tab with collapsible day sections
  const renderHistory = () => (
    <div style={styles.historySection}>
      <h3 style={styles.sectionTitle}>Status Verteilung</h3>
      {renderStatusChart()}
      
      <h3 style={styles.sectionTitle}>Status Verlauf</h3>
      {renderTimelineChart()}
      
      <div style={styles.historyList}>
        {Object.entries(historyByDay).map(([day, records], dayIndex) => {
          const finalStatus = getFinalDayStatus(records);
          const isExpanded = expandedDays[day];
          
          return (
            <motion.div 
              key={day} 
              style={styles.daySection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: dayIndex * 0.05, duration: 0.3 }}
            >
              <motion.div 
                style={styles.daySectionHeader}
                onClick={() => toggleDayExpand(day)}
                whileHover={{ backgroundColor: '#f3f4f6' }}
              >
                <div style={styles.daySectionHeaderLeft}>
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <span style={styles.dayLabel}>{formatDate(day)}</span>
                </div>
                
                {finalStatus && (
                  <div style={{
                    ...styles.finalDayStatus,
                    backgroundColor: STATUS_TYPES[finalStatus]?.color || '#6b7280'
                  }}>
                    <span>Endstatus: {STATUS_TYPES[finalStatus]?.label || finalStatus}</span>
                  </div>
                )}
              </motion.div>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={styles.dayContentWrapper}
                  >
                    <div style={styles.dayContent}>
                      {records.map((record, index) => (
                        <motion.div 
                          key={index} 
                          style={styles.historyItem}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.2 }}
                        >
                          <div style={{
                            ...styles.historyIcon,
                            backgroundColor: STATUS_TYPES[record.newStatus]?.color || '#6b7280'
                          }}>
                            <Clock size={20} />
                          </div>
                          <div style={styles.historyContent}>
                            <div style={styles.historyStatus}>
                              {STATUS_TYPES[record.newStatus]?.label || record.newStatus}
                            </div>
                            <div style={styles.historyTime}>
                              {new Date(record.timestamp).toLocaleTimeString()}
                            </div>
                            <div style={styles.historyUpdater}>
                              Geändert von: {record.updatedBy}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );

  // Render room information card
  const renderRoomCard = () => {
    if (!roomInfo) return null;
    
    // No room assigned
    if (roomInfo.message === 'No room assigned') {
      return (
        <motion.div 
          style={{...styles.roomCard, backgroundColor: '#f3f4f6'}}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <div style={styles.roomCardIcon}>
            <Home size={24} color="#9ca3af" />
          </div>
          <div style={styles.roomCardContent}>
            <h3 style={styles.roomCardTitle}>Zimmer</h3>
            <p style={{...styles.roomCardValue, color: '#6b7280'}}>Kein Zimmer zugewiesen</p>
          </div>
        </motion.div>
      );
    }
    
    // Room assigned
    return (
      <motion.div 
        style={styles.roomCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <div style={styles.roomCardIcon}>
          <Home size={24} color="#3b82f6" />
        </div>
        <div style={styles.roomCardContent}>
          <h3 style={styles.roomCardTitle}>Zimmer</h3>
          <p style={styles.roomCardValue}>
            {roomInfo.roomNumber}
            {roomInfo.building && (
              <span style={styles.buildingInfo}>
                <Building size={14} />
                {roomInfo.building}
              </span>
            )}
          </p>
          
          {roomInfo.roommates && roomInfo.roommates.length > 0 && (
            <div style={styles.roommatesList}>
              <span style={styles.roommatesLabel}>Zimmergenossen:</span>
              {roomInfo.roommates.map((roommate, idx) => (
                <span key={idx} style={styles.roommate}>
                  {roommate.username}
                </span>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // Render overview tab
  const renderOverview = () => (
    <div>
      <div style={mobileView ? styles.statCardsMobile : styles.statCards}>
        <motion.div 
          style={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <div style={styles.statCardIcon}>
            <Calendar size={24} color="#3b82f6" />
          </div>
          <div style={styles.statCardContent}>
            <h3 style={styles.statCardTitle}>User ID</h3>
            <p style={styles.statCardValue}>{user.id}</p>
          </div>
        </motion.div>

        {renderRoomCard()}

        <motion.div 
          style={styles.statCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <div style={styles.statCardIcon}>
            <Activity size={24} color="#10b981" />
          </div>
          <div style={styles.statCardContent}>
            <h3 style={styles.statCardTitle}>Status</h3>
            <p style={{
              ...styles.statCardValue,
              color: STATUS_TYPES[user.status]?.color || '#6b7280'
            }}>
              {STATUS_TYPES[user.status]?.label || user.status}
            </p>
          </div>
        </motion.div>
      </div>

      <motion.div
        style={styles.statusSection}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
      >
        <h3 style={styles.sectionTitle}>Quick Actions</h3>
        <div style={styles.statusActions}>
          <motion.button
            style={{
              ...styles.statusActionButton,
              backgroundColor: '#10b981',
            }}
            onClick={() => {
              setNewStatus('ACTIVE');
              setShowStatusConfirm(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <UserCheck size={16} style={styles.buttonIcon} />
            <span>Im Lager</span>
          </motion.button>
          
          <motion.button
            style={{
              ...styles.statusActionButton,
              backgroundColor: '#3b82f6',
            }}
            onClick={() => {
              setNewStatus('CHECKEDOUT');
              setShowStatusConfirm(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <UserX size={16} style={styles.buttonIcon} />
            <span>Abwesend</span>
          </motion.button>

          <motion.button
            style={{
              ...styles.statusActionButton,
              backgroundColor: user.status === 'SICK' ? '#b91c1c' : '#ef4444',
              boxShadow: user.status === 'SICK' ? '0 0 0 3px rgba(239,68,68,0.35)' : 'none',
            }}
            onClick={() => {
              setNewStatus('SICK');
              setShowStatusConfirm(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <AlertTriangle size={16} style={styles.buttonIcon} />
            <span>Krank</span>
            {user.status === 'SICK' && (
              <span style={{
                fontSize: '10px',
                backgroundColor: 'rgba(255,255,255,0.25)',
                borderRadius: '4px',
                padding: '1px 5px',
                marginLeft: '4px',
                fontWeight: '700'
              }}>
                AKTIV
              </span>
            )}
          </motion.button>

          <motion.button
            style={{
              ...styles.statusActionButton,
              backgroundColor: user.status === 'ABSENT' ? '#c2410c' : '#f97316',
              boxShadow: user.status === 'ABSENT' ? '0 0 0 3px rgba(249,115,22,0.35)' : 'none',
            }}
            onClick={() => {
              setNewStatus('ABSENT');
              setShowStatusConfirm(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <MapPin size={16} style={styles.buttonIcon} />
            <span>Nicht anwesend</span>
            {user.status === 'ABSENT' && (
              <span style={{
                fontSize: '10px',
                backgroundColor: 'rgba(255,255,255,0.25)',
                borderRadius: '4px',
                padding: '1px 5px',
                marginLeft: '4px',
                fontWeight: '700'
              }}>
                AKTIV
              </span>
            )}
          </motion.button>

          <motion.button
            style={{
              ...styles.statusActionButton,
              backgroundColor: '#8b5cf6',
            }}
            onClick={() => {
              setShowLogoutConfirm(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LogOut size={16} style={styles.buttonIcon} />
            <span>Force Logout</span>
          </motion.button>
        </div>
      </motion.div>

      {statistics?.statusDistribution && (
        <motion.div 
          style={styles.chartSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <h3 style={styles.sectionTitle}>Status History</h3>
          <div style={styles.chartContainer}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.statusDistribution).map(([status, count]) => ({
                    name: STATUS_TYPES[status]?.label || status,
                    value: count,
                    status: status
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {Object.entries(statistics.statusDistribution).map((entry, index) => (
                    <Cell 
                      key={index} 
                      fill={STATUS_TYPES[entry[0]]?.color || '#6b7280'} 
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}
    </div>
  );

  // Render edit profile tab
  const renderEditProfile = () => (
    <div style={styles.editProfileSection}>
      <motion.div 
        style={styles.profileImageUpload}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div style={styles.profileImagePreview}>
          <img 
            src={imagePreview || profileImageUrl || '/api/placeholder/200/200'} 
            alt={user.username}
            style={styles.previewImage}
          />
          <motion.button 
            style={styles.uploadButton}
            onClick={handleImagePickerClick}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            disabled={isAdmin && !isEditingSelf && !currentUser?.adminCanEditProfileImage}
          >
            <Camera size={20} />
          </motion.button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageSelect}
        />
        <h3 style={styles.username}>{user.username}</h3>
        <p style={styles.email}>{user.email}</p>
        
        {isAdmin && !isEditingSelf && !currentUser?.adminCanEditProfileImage && (
          <div style={styles.adminNoteCard}>
            <AlertCircle size={16} color="#f59e0b" />
            <p style={styles.adminNoteText}>
              Note: Updating profile image for other users requires additional backend support
            </p>
          </div>
        )}
      </motion.div>

      <motion.div 
        style={styles.editForm}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        {editError && (
          <motion.div 
            style={styles.errorMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <AlertCircle size={16} />
            <span>{editError}</span>
          </motion.div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Username</label>
          <input
            type="text"
            style={styles.formInput}
            value={editedUsername}
            onChange={(e) => setEditedUsername(e.target.value)}
            placeholder="Enter username"
          />
        </div>
        
        {/* Password change section - Only visible for admins editing other users */}
        {isAdmin && !isEditingSelf && (
          <div style={styles.passwordSection}>
            {!showPasswordField ? (
              <motion.button
                style={styles.showPasswordButton}
                onClick={() => setShowPasswordField(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Key size={16} style={styles.buttonIcon} />
                <span>Change User's Password</span>
              </motion.button>
            ) : (
              <>
                {passwordError && (
                  <motion.div 
                    style={styles.errorMessage}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <AlertCircle size={16} />
                    <span>{passwordError}</span>
                  </motion.div>
                )}
                
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>New Password</label>
                  <input
                    type="password"
                    style={styles.formInput}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Confirm Password</label>
                  <input
                    type="password"
                    style={styles.formInput}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>
                
                <div style={styles.passwordActions}>
                  <motion.button
                    style={styles.cancelPasswordButton}
                    onClick={() => {
                      setShowPasswordField(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <X size={16} style={styles.buttonIcon} />
                    <span>Cancel</span>
                  </motion.button>
                  
                  <motion.button
                    style={styles.changePasswordButton}
                    onClick={handleAdminPasswordChange}
                    disabled={passwordChangeLoading}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {passwordChangeLoading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <RefreshCw size={16} />
                        </motion.div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Key size={16} style={styles.buttonIcon} />
                        <span>Change Password</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </>
            )}
          </div>
        )}

        {uploadProgress > 0 && (
          <div style={styles.progressContainer}>
            <div 
              style={{
                ...styles.progressBar,
                width: `${uploadProgress}%`,
                backgroundColor: uploadProgress === 100 ? '#10b981' : '#3b82f6',
              }}
            />
            <span style={styles.progressText}>{uploadProgress}%</span>
          </div>
        )}

        <motion.button
          style={styles.saveButton}
          onClick={saveProfileChanges}
          disabled={editLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {editLoading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <RefreshCw size={16} />
              </motion.div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save size={16} />
              <span>Save Changes</span>
            </>
          )}
        </motion.button>
      </motion.div>
    </div>
  );

  // Render delete confirmation dialog
  const renderDeleteConfirmDialog = () => (
    <div style={styles.confirmDialogOverlay} onClick={() => setShowDeleteConfirm(false)}>
      <motion.div 
        style={styles.confirmDialog}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.confirmTitle}>
          <AlertOctagon size={24} color="#ef4444" />
          <span>Delete User</span>
        </div>
        <div style={styles.confirmText}>
          Are you sure you want to delete user <strong>{user.username}</strong>? 
          This action cannot be undone.
        </div>
        
        {deleteError && (
          <div style={styles.deleteError}>
            <AlertCircle size={16} />
            <span>{deleteError}</span>
          </div>
        )}
        
        <div style={styles.confirmButtons}>
          <motion.button 
            style={styles.cancelButton}
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Cancel
          </motion.button>
          <motion.button 
            style={styles.confirmDeleteButton}
            onClick={handleDeleteUser}
            disabled={deleteLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {deleteLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw size={16} />
                </motion.div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 size={16} />
                <span>Delete</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  // Render logout confirmation dialog
  const renderLogoutConfirmDialog = () => (
    <div style={styles.confirmDialogOverlay} onClick={() => setShowLogoutConfirm(false)}>
      <motion.div 
        style={styles.confirmDialog}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.confirmTitle}>
          <LogOut size={24} color="#8b5cf6" />
          <span>Force Logout</span>
        </div>
        <div style={styles.confirmText}>
          Are you sure you want to force <strong>{user.username}</strong> to log out? 
          The user will need to sign in again to access the app.
        </div>
        
        {logoutError && (
          <div style={styles.deleteError}>
            <AlertCircle size={16} />
            <span>{logoutError}</span>
          </div>
        )}
        
        <div style={styles.confirmButtons}>
          <motion.button 
            style={styles.cancelButton}
            onClick={() => setShowLogoutConfirm(false)}
            disabled={logoutLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Cancel
          </motion.button>
          <motion.button 
            style={{
              ...styles.confirmActionButton,
              backgroundColor: '#8b5cf6',
            }}
            onClick={forceUserLogout}
            disabled={logoutLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {logoutLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw size={16} />
                </motion.div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <LogOut size={16} />
                <span>Force Logout</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  // Render status change confirmation dialog
  const renderStatusConfirmDialog = () => (
    <div style={styles.confirmDialogOverlay} onClick={() => setShowStatusConfirm(false)}>
      <motion.div 
        style={styles.confirmDialog}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.confirmTitle}>
          {newStatus === 'SICK'
            ? <AlertTriangle size={24} color="#ef4444" />
            : <Activity size={24} color={STATUS_TYPES[newStatus]?.color || '#6b7280'} />
          }
          <span>Status ändern</span>
        </div>
        <div style={styles.confirmText}>
          Status von <strong>{user.username}</strong> wirklich auf{' '}
          <strong style={{ color: STATUS_TYPES[newStatus]?.color || '#6b7280' }}>
            {STATUS_TYPES[newStatus]?.label || newStatus}
          </strong>{' '}
          setzen?
          {newStatus === 'SICK' && (
            <div style={{
              marginTop: '10px',
              padding: '8px 12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              gap: '7px'
            }}>
              <AlertTriangle size={14} />
              Der Benutzer wird als krank markiert und im Lager als abwesend geführt.
            </div>
          )}
        </div>
        
        {statusError && (
          <div style={styles.deleteError}>
            <AlertCircle size={16} />
            <span>{statusError}</span>
          </div>
        )}
        
        <div style={styles.confirmButtons}>
          <motion.button
            style={styles.cancelButton}
            onClick={() => setShowStatusConfirm(false)}
            disabled={statusLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Abbrechen
          </motion.button>
          <motion.button
            style={{
              ...styles.confirmActionButton,
              backgroundColor: STATUS_TYPES[newStatus]?.color || '#6b7280',
            }}
            onClick={() => updateUserStatus(newStatus)}
            disabled={statusLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {statusLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw size={16} />
                </motion.div>
                <span>Wird gesetzt…</span>
              </>
            ) : (
              <>
                <Check size={16} />
                <span>Bestätigen</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  // Layout component
  const Layout = ({ size, ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="3" y1="9" x2="21" y2="9"></line>
      <line x1="9" y1="21" x2="9" y2="9"></line>
    </svg>
  );

  if (!user) return null;

  return (
    <AnimatePresence>
      <motion.div
        style={styles.modalOverlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          style={{
            ...styles.modal,
            ...(mobileView ? styles.modalMobile : {})
          }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>User Details</h2>
            <motion.button 
              onClick={onClose} 
              style={styles.closeButton}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={24} />
            </motion.button>
          </div>

          {/* Mobile User Profile Header - only for mobile view */}
          {mobileView && (
            <motion.div 
              style={styles.mobileProfileHeader}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div style={styles.mobileProfileImage}>
                <img 
                  src={profileImageUrl || '/api/placeholder/100/100'} 
                  alt={user.username}
                  style={styles.mobileAvatar}
                />
                <div style={{
                  ...styles.mobileStatusIndicator,
                  backgroundColor: STATUS_TYPES[user.status]?.color || '#6b7280'
                }}></div>
              </div>
              <div style={styles.mobileProfileInfo}>
                <h3 style={styles.mobileProfileName}>{user.username}</h3>
                <p style={styles.mobileProfileEmail}>{user.email}</p>
                <div style={{
                  ...styles.mobileStatusBadge,
                  backgroundColor: STATUS_TYPES[user.status]?.color || '#6b7280'
                }}>
                  {STATUS_TYPES[user.status]?.label || user.status}
                </div>
                
                {/* Show room info in mobile view */}
                {roomInfo && roomInfo.roomNumber && (
                  <div style={styles.mobileRoomInfo}>
                    <Home size={14} />
                    <span>Zimmer {roomInfo.roomNumber}</span>
                    {roomInfo.building && <span> ({roomInfo.building})</span>}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Success message */}
          <AnimatePresence>
            {successMessage && (
              <motion.div 
                style={styles.successMessage}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <CheckCircle size={20} />
                <span>{successMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Admin indicator if editing another user */}
          {isAdmin && !isEditingSelf && (
            <motion.div 
              style={styles.adminIndicator}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Shield size={16} />
              <span>You are editing this user as an administrator</span>
            </motion.div>
          )}

          {/* User profile section - desktop view */}
          {!mobileView && (
            <div style={styles.profileSection}>
              <div style={styles.imageContainer}>
                <motion.img 
                  src={profileImageUrl || '/api/placeholder/100/100'}
                  alt={user.username}
                  style={styles.profileImage}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  onError={(e) => {
                    e.target.src = '/api/placeholder/100/100';
                    setImageLoaded(false);
                  }}
                />
              </div>

              <div style={styles.userInfo}>
                <motion.h2 
                  style={styles.userName}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  {user.username}
                </motion.h2>
                
                <motion.div 
                  style={styles.userEmail}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <Mail size={16} />
                  {user.email}
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <div style={{
                    ...styles.statusBadge,
                    backgroundColor: STATUS_TYPES[user.status]?.color || '#6b7280'
                  }}>
                    {STATUS_TYPES[user.status]?.label || user.status}
                  </div>
                </motion.div>
                
                {/* Room information in desktop profile */}
                {roomInfo && roomInfo.roomNumber && (
                  <motion.div
                    style={styles.roomBadge}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35, duration: 0.3 }}
                  >
                    <Home size={14} />
                    <span>Zimmer {roomInfo.roomNumber}</span>
                    {roomInfo.building && <span> ({roomInfo.building})</span>}
                  </motion.div>
                )}

                <motion.button 
                  style={styles.deleteButton}
                  onClick={() => setShowDeleteConfirm(true)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Trash2 size={16} />
                  Delete User
                </motion.button>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div style={styles.tabsContainer}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'overview' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('overview')}
            >
              <Layout size={16} />
              <span>Overview</span>
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'edit' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('edit')}
            >
              <Edit3 size={16} />
              <span>Edit Profile</span>
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'history' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('history')}
            >
              <Clock size={16} />
              <span>History</span>
            </button>
          </div>

          {/* Tab Content */}
          <div style={styles.tabContent}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Activity size={40} />
                </motion.div>
                <p style={styles.loadingText}>Loading user data...</p>
              </div>
            ) : error ? (
              <div style={styles.errorContainer}>
                <AlertTriangle size={40} />
                <p style={styles.errorText}>{error}</p>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'edit' && renderEditProfile()}
                {activeTab === 'history' && renderHistory()}
              </>
            )}
          </div>

          {/* Mobile action buttons */}
          {mobileView && (
            <div style={styles.mobileActions}>
              <motion.button 
                style={{
                  ...styles.mobileActionButton,
                  backgroundColor: '#10b981',
                }}
                onClick={() => {
                  setNewStatus('ACTIVE');
                  setShowStatusConfirm(true);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserCheck size={20} />
              </motion.button>
              
              <motion.button 
                style={{
                  ...styles.mobileActionButton,
                  backgroundColor: '#3b82f6',
                }}
                onClick={() => {
                  setNewStatus('CHECKEDOUT');
                  setShowStatusConfirm(true);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserX size={20} />
              </motion.button>
              
              <motion.button 
                style={{
                  ...styles.mobileActionButton,
                  backgroundColor: '#8b5cf6',
                }}
                onClick={() => setShowLogoutConfirm(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogOut size={20} />
              </motion.button>
              
              <motion.button 
                style={{
                  ...styles.mobileActionButton,
                  backgroundColor: '#ef4444',
                }}
                onClick={() => setShowDeleteConfirm(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Trash2 size={20} />
              </motion.button>
            </div>
          )}
        </motion.div>

        {/* Confirmation Dialogs */}
        <AnimatePresence>
          {showDeleteConfirm && renderDeleteConfirmDialog()}
          {showLogoutConfirm && renderLogoutConfirmDialog()}
          {showStatusConfirm && renderStatusConfirmDialog()}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

// Define styles
const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalMobile: {
    width: '95%',
    maxWidth: '450px',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    color: '#6b7280',
    transition: 'all 0.2s',
  },
  // Admin indicator
  adminIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#f0f9ff',
    color: '#0284c7',
    margin: '10px 20px 0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    border: '1px solid #e0f2fe',
  },
  // Mobile profile header
  mobileProfileHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
    position: 'relative',
    marginBottom: '20px',
  },
  mobileProfileImage: {
    position: 'relative',
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    marginBottom: '15px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  },
  mobileAvatar: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #ffffff',
  },
  mobileStatusIndicator: {
    position: 'absolute',
    bottom: '3px',
    right: '3px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #ffffff',
  },
  mobileProfileInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  mobileProfileName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 5px 0',
  },
  mobileProfileEmail: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 10px 0',
  },
  mobileStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#ffffff',
    marginBottom: '8px',
  },
  mobileRoomInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#3b82f6',
    padding: '4px 10px',
    borderRadius: '6px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
  },
  // Success message
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    backgroundColor: '#d1fae5',
    color: '#10b981',
    borderRadius: '8px',
    margin: '15px 20px 0',
  },
  // Profile section
  profileSection: {
    display: 'flex',
    gap: '24px',
    padding: '24px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
  },
  imageContainer: {
    width: '220px',
    height: '220px',
    borderRadius: '50%',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '4px solid #ffffff',
  },
  profileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  userInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  userName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  userEmail: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#6b7280',
    fontSize: '14px',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    width: 'fit-content',
  },
  roomBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    width: 'fit-content',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    width: 'fit-content',
    marginTop: '10px',
    transition: 'all 0.2s ease',
  },
  // Tabs
  tabsContainer: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
  },
  tab: {
    flex: 1,
    padding: '14px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  activeTab: {
    color: '#3b82f6',
    borderBottom: '2px solid #3b82f6',
    backgroundColor: '#f0f5ff',
  },
  // Tab content
  tabContent: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  // Loading and error states
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 0',
    color: '#6b7280',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 0',
    color: '#ef4444',
  },
  errorText: {
    marginTop: '20px',
    fontSize: '16px',
  },
  // History section
  historySection: {
    padding: '0'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '16px',
  },
  chartContainer: {
    marginTop: '20px',
    marginBottom: '24px',
    backgroundColor: '#f8fafc',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '24px',
  },
  daySection: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
    backgroundColor: '#ffffff',
  },
  daySectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  daySectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dayLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827',
  },
  finalDayStatus: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#ffffff',
  },
  dayContentWrapper: {
    overflow: 'hidden',
  },
  dayContent: {
    padding: '10px 16px 16px',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  historyItem: {
    display: 'flex',
    gap: '16px',
    padding: '14px',
    backgroundColor: '#f9fafb',
    borderRadius: '10px',
  },
  historyIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    flexShrink: 0,
  },
  historyContent: {
    flex: 1
  },
  historyStatus: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '4px',
  },
  historyTime: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '4px',
  },
  historyUpdater: {
    fontSize: '13px',
    color: '#6b7280',
  },
  // Edit profile section
  editProfileSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  profileImageUpload: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    position: 'relative',
  },
  profileImagePreview: {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    overflow: 'hidden',
    position: 'relative',
    border: '4px solid #ffffff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    marginBottom: '15px',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },uploadButton: {
    position: 'absolute',
    bottom: '5px',
    right: '5px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px solid #ffffff',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  username: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '5px 0',
  },
  email: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  adminNoteCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: '#fffbeb',
    borderRadius: '8px',
    fontSize: '12px',
    marginTop: '10px',
    border: '1px solid #fef3c7',
  },
  adminNoteText: {
    color: '#92400e',
    margin: 0,
    fontSize: '12px',
    lineHeight: 1.5,
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    color: '#111827',
    backgroundColor: '#f9fafb',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  // Password section
  passwordSection: {
    marginBottom: '15px',
    padding: '15px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
  },
  showPasswordButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#8b5cf6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  passwordActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '15px',
  },
  cancelPasswordButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  changePasswordButton: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#8b5cf6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  progressBar: {
    height: '8px',
    borderRadius: '4px',
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },
  progressText: {
    fontSize: '12px',
    color: '#6b7280',
    textAlign: 'right',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    borderRadius: '8px',
    fontSize: '14px',
  },
  // Overview tab
  statCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '30px',
  },
  statCardsMobile: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '30px',
  },
  statCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  roomCard: {
    backgroundColor: '#f0f7ff',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  statCardIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  roomCardIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statCardContent: {
    flex: 1,
  },
  roomCardContent: {
    flex: 1,
  },
  statCardTitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 5px 0',
  },
  roomCardTitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 5px 0',
  },
  statCardValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  roomCardValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3b82f6',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buildingInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: '#4b5563',
    marginLeft: '8px',
  },
  roommatesList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  roommatesLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  roommate: {
    fontSize: '14px',
    color: '#4b5563',
    padding: '4px 8px',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    borderRadius: '6px',
    display: 'inline-block',
    marginRight: '6px',
    marginBottom: '6px',
  },
  statusSection: {
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px',
  },
  statusActions: {
    display: 'flex',
    gap: '16px',
    marginTop: '15px',
  },
  statusActionButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: 'none',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  chartSection: {
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '20px',
  },
  // Mobile action buttons at the bottom
  mobileActions: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '15px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc',
  },
  mobileActionButton: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  // Confirmation dialogs
  confirmDialogOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1100,
  },
  confirmDialog: {
    position: 'relative',
    backgroundColor: '#ffffff',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    width: '90%',
    maxWidth: '400px',
    zIndex: 1101,
  },
  confirmTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  confirmText: {
    marginBottom: '24px',
    color: '#4b5563',
    lineHeight: 1.5,
    fontSize: '15px',
  },
  confirmButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '10px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  confirmDeleteButton: {
    padding: '10px 16px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  confirmActionButton: {
    padding: '10px 16px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  deleteError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '16px',
  },
  buttonIcon: {
    flexShrink: 0,
  },
};

export default UserModal;